/**
 * TinyMCE 래퍼 — 레거시 announcementsForm Editor 설정 포팅.
 */
import { boardService } from '@/entities/board/api/board.api';
import { cn } from '@/shared/lib/utils';
import { Editor } from '@tinymce/tinymce-react';
import React, { useEffect, useMemo, useRef } from 'react';

export interface TinyMceEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  className?: string;
  height?: number;
  /** 본문 내 이미지 업로드 (기본: 공지 이미지 API) */
  onImageUpload?: (file: Blob) => Promise<string>;
  /**
   * 에디터가 입력을 받을 수 있는 상태가 된 시점.
   * API 키가 없어 에디터를 띄우지 못하는 경우에도 호출해, 호출부가 대기 상태에 갇히지 않게 한다.
   */
  onReady?: () => void;
}

declare global {
  interface Window {
    tinymce?: {
      activeEditor?: {
        editorUpload: {
          blobCache: {
            create: (
              id: string,
              file: File,
              base64: string,
            ) => { blobUri: () => string };
            add: (blobInfo: { blobUri: () => string }) => void;
          };
        };
      };
    };
  }
}

const toUploadFile = (file: Blob): File =>
  file instanceof File
    ? file
    : new File([file], 'editor-image.jpg', {
        type: file.type || 'image/jpeg',
      });

export const TinyMceEditor: React.FC<TinyMceEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  className,
  height = 800,
  onImageUpload,
  onReady,
}) => {
  const apiKey = import.meta.env.VITE_TINY_MCE_API_KEY as string | undefined;
  const uploadRef = useRef(onImageUpload);
  uploadRef.current = onImageUpload;
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  // 에디터를 렌더하지 않는 경우(키 없음·읽기 전용)에는 즉시 준비 완료로 알린다
  useEffect(() => {
    if (!apiKey || readOnly) readyRef.current?.();
  }, [apiKey, readOnly]);

  const init = useMemo(
    () => ({
      plugins: [
        'autolink',
        'lists',
        'advlist',
        'link',
        'image',
        'anchor',
        'searchreplace',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'code',
      ],
      toolbar:
        'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image table tablecellvalign | align lineheight | checklist numlist bullist indent outdent | code',
      image_description: false,
      image_file_types: 'jpg,jpeg,png,svg,webp',
      file_picker_types: 'image',
      images_upload_handler: (blobInfo: {
        blob: () => Blob;
      }): Promise<string> => {
        const blob = blobInfo.blob();
        const custom = uploadRef.current;
        if (custom) return custom(blob);
        return boardService.uploadAnnouncementImage(toUploadFile(blob));
      },
      file_picker_callback: (
        cb: (
          url: string,
          meta?: { title?: string; width?: string; height?: string },
        ) => void,
      ) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.addEventListener('change', () => {
          const file = input.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            const id = `blobid${Date.now()}`;
            const blobCache =
              window.tinymce?.activeEditor?.editorUpload.blobCache;
            const result = String(reader.result ?? '');
            const base64 = result.split(',')[1] ?? '';
            if (blobCache) {
              const blobInfo = blobCache.create(id, file, base64);
              blobCache.add(blobInfo);
              cb(blobInfo.blobUri(), {
                title: file.name,
                width: '100%',
                height: 'auto',
              });
              return;
            }
            cb(result, {
              title: file.name,
              width: '100%',
              height: 'auto',
            });
          });
          reader.readAsDataURL(file);
        });
        input.click();
      },
      image_dimensions: false,
      language: 'ko_KR',
      language_url: '/tinymce/langs/ko_KR.js',
      spellchecker_language: 'ko',
      date_format: 'Y-m-d',
      time_format: 'H:i',
      content_language: 'ko',
      spellchecker_languages: 'ko=한국어,en=English',
      width: '100%',
      height,
      resize: true,
      branding: false,
      promotion: false,
    }),
    [height],
  );

  if (!apiKey) {
    return (
      <div
        className={cn(
          'rounded-12 border border-ruda-border-light bg-ruda-grey-50 p-4 text-sm text-ruda-text-secondary',
          className,
        )}
      >
        TinyMCE API 키(`VITE_TINY_MCE_API_KEY`)가 설정되지 않았습니다.
      </div>
    );
  }

  if (readOnly) {
    return (
      <div
        className={cn(
          'min-h-[200px] rounded-12 border border-ruda-border-light bg-ruda-grey-50 px-3 py-2 text-sm',
          className,
        )}
        dangerouslySetInnerHTML={{ __html: value || '<p>-</p>' }}
      />
    );
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-12 border border-ruda-border-light',
        className,
      )}
    >
      <Editor
        apiKey={apiKey}
        value={value}
        init={init}
        disabled={readOnly}
        onInit={() => readyRef.current?.()}
        onEditorChange={(content) => onChange(content)}
      />
    </div>
  );
};
